import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, ThemeMenu } from '@easydent/shared-ui';
import { useAuthStore } from '@/stores/authStore';
import { login } from '@/lib/api';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAuth, clearAuth } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [needs2fa, setNeeds2fa] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await login(email.trim(), password, needs2fa ? code.trim() : undefined);
      if (result.kind === '2fa') {
        setNeeds2fa(true);
        return;
      }
      if (result.role !== 'SuperAdmin') {
        clearAuth();
        setError(t('login.operatorsOnly', 'This console is for platform operators only.'));
        return;
      }
      setAuth(result.accessToken, result.role);
      navigate('/', { replace: true });
    } catch {
      setError(needs2fa
        ? t('login.badCode', 'Invalid authenticator code.')
        : t('login.badCredentials', 'Invalid email or password.'));
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full rounded-lg border border-line-strong px-3 py-2 text-sm focus:border-brand-500 focus:outline-none';

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-line bg-surface p-8 shadow-sm">
        {/* Before sign-in is exactly when this is needed: the app is a login form and
            nothing else, so a dark-mode user would otherwise meet a white screen with
            nowhere to change it. */}
        <div className="mb-4 flex justify-end">
          <ThemeMenu labels={{ label: t('theme.label', 'Theme'), light: t('theme.light', 'Light'), dark: t('theme.dark', 'Dark'), system: t('theme.system', 'System') }} />
        </div>
        <div className="mb-6 flex items-center gap-2 text-xl font-bold text-brand-ink-strong">
          <span>⬡</span> easyDent <span className="font-normal text-subtle-ink">{t('portalName', "Operator")}</span>
        </div>
        {error && <Alert variant="error" className="mb-3">{error}</Alert>}
        <label className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">{t('login.email', "Email")}</label>
        <input type="email" autoComplete="username" disabled={needs2fa} className={`${field} mb-3`} value={email} onChange={(e) => setEmail(e.target.value)} required />
        <label className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">{t('login.password', "Password")}</label>
        <input type="password" autoComplete="current-password" disabled={needs2fa} className={`${field} mb-5`} value={password} onChange={(e) => setPassword(e.target.value)} required />
        {needs2fa && (
          <>
            <label className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">{t('login.totp', "Authenticator code")}</label>
            <input
              inputMode="numeric" autoComplete="one-time-code" autoFocus
              className={`${field} mb-5 tracking-widest`} value={code}
              onChange={(e) => setCode(e.target.value)} placeholder="123456" required
            />
          </>
        )}
        <Button type="submit" className="w-full" loading={busy}>{needs2fa ? t('login.verify', 'Verify') : t('login.submit', 'Sign in')}</Button>
        {/* Hidden once a code is being asked for: at that point the password already worked,
            so offering to reset it would only send someone down the wrong path. */}
        {!needs2fa && (
          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm text-subtle-ink hover:text-muted-strong">
              {t('login.forgot', 'Forgot your password?')}
            </Link>
          </div>
        )}
      </form>
    </div>
  );
};

export default LoginPage;
